/** Clearing an overdue backlog from the Visit board: move a selection to one date, or skip it with one reason. */
import { inject, Injector, runInInjectionContext } from '@angular/core';
import { ActivityEntityName, JobSecurityRight, MetadataEntityFieldType, SectionLayout, SurfaceType, VISIT_PAST_RETENTION_DAYS, VisitAgeState, VisitBrowseCommandId, VisitBrowseRow, visitBrowseCapabilities } from '@jms/model';
import { VISIT_DATES_COMMAND, VisitDateOverride, VisitDatesRequest, visitRescheduleAllowed, visitRescheduleFloor } from '@jms/model/entities/planned.work/visit/visit.dates';
import { RightsService } from '@jms/utils/util.permissions';
import { EntityDataService } from '@workbuddy/services/entities';
import { Command } from '@workbuddy/ui/model';
import { ToastService } from '@workbuddy/ui/services';
import { injectDialog } from '@workbuddy/ui/v2/blocks/dialog';
import type { BulkCommandArgs, BulkCommandOutcome, EntityBoardBulkCommandsArgs } from '@workbuddy/ui/v2/blocks/entity.board/context';
import { defineSurface, defineSurfaceSchema, openFormSurfaceDialog } from '@workbuddy/ui/v2/surface.engine';
import { DateTime } from 'luxon';
import { visitDatesEditable } from './visit.dates.command.support';
import { visitRescheduleRuleText } from '../dialogs/visit.dates/visit.dates.dialog';
import { promptVisitSkipReason } from './visit.skip.reason.prompt';

export const VISIT_OVERDUE_RESCHEDULE_BULK_ID = 'visit.overdue.reschedule';
export const VISIT_OVERDUE_SKIP_BULK_ID = 'visit.overdue.skip';
/** One person resolving a page of backlog; a larger selection is a filter problem, not a bulk one. */
export const MAX_VISIT_OVERDUE_BULK = 50;
// Each row is its own domain command; a few in flight keeps a 50-row batch quick without flooding the API.
const CONCURRENCY = 4;

type RunOverdueBulk = (rows: readonly VisitBrowseRow[]) => Promise<boolean>;

interface BulkRescheduleForm { date: Date; }

interface RowFailure { row: VisitBrowseRow; message: string; }

export function visitOverdueBulkCommands(args: EntityBoardBulkCommandsArgs<VisitBrowseRow>): Command[] {

  const rights = inject(RightsService);
  if (!rights.hasRights(JobSecurityRight.CanAccess) || !rights.hasRights(JobSecurityRight.Manage)) return [];
  const selected = args.selectedItems ?? [];
  const injector = inject(Injector);
  const commands: Command[] = [];
  if (withinCap(selected.filter(reschedulable))) {
    commands.push(new Command({ _id: VISIT_OVERDUE_RESCHEDULE_BULK_ID, label: 'Reschedule…', iconLeft: 'fa-regular fa-calendar', data: { run: rows => rescheduleVisits(injector, rows) } satisfies { run: RunOverdueBulk } }));
  }
  if (withinCap(selected.filter(skippable))) {
    commands.push(new Command({ _id: VISIT_OVERDUE_SKIP_BULK_ID, label: 'Skip…', iconLeft: 'fa-regular fa-ban', data: { run: rows => skipVisits(injector, rows) } satisfies { run: RunOverdueBulk } }));
  }
  return commands;
}

export function runVisitOverdueRescheduleBulk(args: BulkCommandArgs<VisitBrowseRow>): Promise<BulkCommandOutcome | false> {

  return runBulk(args, reschedulable);
}

export function runVisitOverdueSkipBulk(args: BulkCommandArgs<VisitBrowseRow>): Promise<BulkCommandOutcome | false> {

  return runBulk(args, skippable);
}

/**
 * Keeps each Visit's own window times on the chosen local day, in the Visit's timezone. The end keeps
 * its day offset from the start rather than a duration, so an overnight window stays overnight and a
 * DST change between the two dates does not shift the finish time.
 */
export function visitWindowOnDate(row: VisitBrowseRow, localDate: string): VisitDateOverride {

  const day = DateTime.fromISO(localDate, { zone: row.timezone }).startOf('day');
  if (row.allDay) return { startDate: day.toJSDate(), endDate: day.plus({ days: 1 }).toJSDate(), allDay: true };
  const start = DateTime.fromJSDate(new Date(row.startDate), { zone: row.timezone });
  const end = DateTime.fromJSDate(new Date(row.endDate), { zone: row.timezone });
  const spanDays = Math.max(0, Math.round(end.startOf('day').diff(start.startOf('day'), 'days').days));
  const nextStart = day.set({ hour: start.hour, minute: start.minute });
  const nextEnd = day.plus({ days: spanDays }).set({ hour: end.hour, minute: end.minute });
  return { startDate: nextStart.toJSDate(), endDate: nextEnd.toJSDate(), allDay: false };
}

async function runBulk(args: BulkCommandArgs<VisitBrowseRow>, eligible: (row: VisitBrowseRow) => boolean): Promise<BulkCommandOutcome | false> {

  const run = (args.commandData as { run?: RunOverdueBulk } | undefined)?.run;
  if (!run) return false;
  const rows = args.data.filter(eligible);
  if (!withinCap(rows)) return { handled: true, clearSelection: false };
  return { handled: true, clearSelection: await run(rows) };
}

/** Age is the gate: a Current Visit already has the per-record Reschedule and Skip. */
function aged(row: VisitBrowseRow): boolean {

  return row.ageState === VisitAgeState.Overdue || row.ageState === VisitAgeState.Missed;
}

export function reschedulable(row: VisitBrowseRow): boolean {

  return aged(row) && visitDatesEditable(row);
}

export function skippable(row: VisitBrowseRow): boolean {

  return aged(row) && visitBrowseCapabilities(row).skip;
}

function withinCap(rows: readonly VisitBrowseRow[]): boolean {

  return rows.length > 0 && rows.length <= MAX_VISIT_OVERDUE_BULK;
}

export async function rescheduleVisits(injector: Injector, rows: readonly VisitBrowseRow[]): Promise<boolean> {

  const [first] = rows;
  const now = new Date();
  const timezone = first.timezone;
  const today = DateTime.fromJSDate(now, { zone: timezone }).startOf('day');
  const pastDateRules = visitRescheduleRuleText(now, timezone);
  const count = rows.length === 1 ? 'visit' : `${rows.length} visits`;
  const dialogs = runInInjectionContext(injector, injectDialog);
  const localDate = await openFormSurfaceDialog<string>(dialogs, {
    title: `Reschedule ${count}`,
    createDefinition: api => defineSurface<BulkRescheduleForm>({
      schema: defineSurfaceSchema(ActivityEntityName.Visit, SurfaceType.Form)
        .model<BulkRescheduleForm>()
        .submitLabel('Reschedule')
        .body(body => body.section('date', section => section
          .layout(SectionLayout.PropertyList)
          .columns(1)
          .description(`Each visit keeps its own start and end times. ${pastDateRules}`)
          // The picker's Today button is the shortcut; the retention floor is the earliest any row can take.
          .field({ bind: 'date', required: true, autofocus: true, metadata: { type: MetadataEntityFieldType.Date, displayName: 'New date' }, editorInputs: { timezone, min: today.minus({ days: VISIT_PAST_RETENTION_DAYS }).toISODate()! } })))
        .build(),
      // Today is the usual answer for a backlog, so it is the starting value.
      initialModel: { date: today.toJSDate() },
      onCancel: () => api.dismiss(),
      onSubmit: async event => {
        const chosen = DateTime.fromJSDate(new Date(event.model.date), { zone: timezone }).startOf('day');
        if (!chosen.isValid) throw new Error('Choose a date.');
        const iso = chosen.toISODate()!;
        if (!rows.some(row => visitRescheduleAllowed(row, iso, now))) throw new Error(pastDateRules);
        api.close(iso);
        return event.model;
      },
    }),
  });
  if (!localDate) return false;
  return runVisitsPerRow(injector, rows, {
    title: 'Reschedule',
    done: count => `Rescheduled ${count}.`,
    run: async (data, row) => {
      // The server applies the same rule; refusing here names the row instead of surfacing a 400.
      if (!visitRescheduleAllowed(row, localDate, now)) throw new Error(`${row.number || 'A visit'} cannot move before ${DateTime.fromISO(visitRescheduleFloor(row, now)).toFormat('d LLL yyyy')}.`);
      const request: VisitDatesRequest = { id: row._id, restore: false, dates: visitWindowOnDate(row, localDate) };
      await data.executeCommand<VisitBrowseRow>(ActivityEntityName.Visit, VISIT_DATES_COMMAND, request);
    },
  });
}

export async function skipVisits(injector: Injector, rows: readonly VisitBrowseRow[]): Promise<boolean> {

  const count = rows.length === 1 ? 'visit' : `${rows.length} visits`;
  const answer = await promptVisitSkipReason(injector, {
    title: `Skip ${count}`,
    description: 'Skipped visits stay in history and are not projected again. Age alone never skips a visit, so say why.',
  });
  if (!answer) return false;
  return runVisitsPerRow(injector, rows, {
    title: 'Skip',
    done: count => `Skipped ${count}.`,
    run: async (data, row) => {
      await data.executeCommand<VisitBrowseRow>(ActivityEntityName.Visit, VisitBrowseCommandId.Skip, { id: row.visitId ?? row._id, ...answer });
    },
  });
}

/** Resolves true when anything changed, so the board clears the selection and rereads. */
export async function runVisitsPerRow(injector: Injector, rows: readonly VisitBrowseRow[], options: { title: string; done: (count: string) => string; run: (data: EntityDataService, row: VisitBrowseRow) => Promise<void> }): Promise<boolean> {

  const data = injector.get(EntityDataService);
  const toasts = injector.get(ToastService);
  const failures: RowFailure[] = [];
  const queue = [...rows];
  const worker = async () => {
    for (let row = queue.shift(); row; row = queue.shift()) {
      try {
        await options.run(data, row);
      } catch (error) {
        failures.push({ row, message: error instanceof Error ? error.message : 'The visit could not be updated.' });
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, rows.length) }, worker));
  const succeeded = rows.length - failures.length;
  if (succeeded) {
    data.invalidate(ActivityEntityName.Visit);
    await data.invalidateItems(ActivityEntityName.UpcomingWork);
  }
  const plural = (n: number) => n === 1 ? '1 visit' : `${n} visits`;
  if (!failures.length) toasts.success(options.title, options.done(plural(succeeded)));
  else if (!succeeded) toasts.error(options.title, `None of the selected visits could be updated: ${failures[0].message}`);
  else toasts.warning(options.title, `${options.done(`${succeeded} of ${plural(rows.length)}`)} ${failures.length} could not be updated: ${failures[0].message}`);
  return succeeded > 0;
}
